import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DeviceFrame from "@/components/DeviceFrame";
import FloatingAgent from "@/components/FloatingAgent";
import AgentDock from "@/components/AgentDock";
import PerfectDayCelebration from "@/components/PerfectDayCelebration";
import IntentHandler from "@/components/IntentHandler";
import { preloadOrb } from "@/lib/agentOrb";

/**
 * Protected — gate that redirects unauthenticated users to /login.
 *
 * While the initial /me check is in flight we render a minimalist loader
 * inside the page background so we don't flash the login screen for a frame.
 *
 * When the user is authenticated we also mount the global floating agent,
 * dock, and perfect-day celebration overlay. These are tightly coupled to
 * `AgentContext` (mounted higher in App.js) and must live inside a single
 * shared parent so the orb's anchor rect stays in sync with the dock.
 *
 * `IntentHandler` listens for `?intent=…` query params on every authenticated
 * navigation and dispatches the matching action.
 */
export default function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Kick off orb image warm-up as soon as we know the user is logged in.
  useEffect(() => {
    if (user) preloadOrb();
  }, [user]);

  if (loading) {
    return (
      <div
        className="nx-page-bg min-h-screen w-full flex items-center justify-center"
        data-testid="protected-loading"
      >
        <Loader2
          className="h-7 w-7 text-[#00E5FF]/80 animate-spin"
          strokeWidth={1.75}
        />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <>
      <DeviceFrame>{children}</DeviceFrame>
      <FloatingAgent />
      <AgentDock />
      <PerfectDayCelebration />
      <IntentHandler />
    </>
  );
}
